import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { sendProfileOtp, verifyProfileOtp, resetOtp, otpErr, PROFILE_OTP_PHONE } from "@/services/otp";

// Profile password + fresh OTP to the owner number. gate = { title, description, run({ password, otp_id_token, note }) }
export function CmsPublishGate({ gate, onClose }) {
  const [pw, setPw] = useState("");
  const [note, setNote] = useState("");
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!gate) return null;
  const close = () => { setPw(""); setNote(""); setOtp(""); setSent(false); resetOtp(); onClose(); };
  const send = async () => {
    if (!pw) return toast.error("Enter the profile password first");
    setBusy(true);
    try { await sendProfileOtp("cms-recaptcha"); setSent(true); toast.success(`OTP sent to ${PROFILE_OTP_PHONE}`); }
    catch (e) { toast.error(otpErr(e)); resetOtp(); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const token = await verifyProfileOtp(otp.trim());
      await gate.run({ password: pw, otp_id_token: token, note: note.trim() || null });
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || otpErr(e) || e.message);
      setSent(false); setOtp("");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="cms-gate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Icons.ShieldCheck className="h-5 w-5 text-primary" /> {gate.title}</DialogTitle>
          <DialogDescription>{gate.description || "Requires the profile password and an OTP sent to"} <span className="font-mono">{PROFILE_OTP_PHONE}</span>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5"><Label>Profile password *</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} disabled={sent} data-testid="cms-gate-password" /></div>
        <div className="space-y-1.5"><Label>Change note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Updated hero image" data-testid="cms-gate-note" /></div>
        {sent && <div className="space-y-1.5"><Label>OTP *</Label><Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" data-testid="cms-gate-otp" /></div>}
        <div id="cms-recaptcha" />
        <DialogFooter>
          <Button variant="ghost" onClick={close} data-testid="cms-gate-cancel">Cancel</Button>
          {!sent
            ? <Button onClick={send} disabled={busy || !pw} data-testid="cms-gate-send-otp"><Icons.Smartphone className="h-4 w-4 mr-1" /> {busy ? "Sending…" : "Send OTP"}</Button>
            : <Button onClick={confirm} disabled={busy || !otp.trim()} data-testid="cms-gate-confirm">{busy ? "Publishing…" : "Verify & publish"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
